import type { Key } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LinkButton,
  SectionLayout,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@breezeai-frontend/cargo-ui';

import { useUser } from '../../../context/auth/auth-hooks';
import { useTracking } from '../../../utils/snowplow/hooks';
import { DeclarationCancellationModal } from '../declarations/DeclarationCancellationModal';
import { DeclarationCreationModal } from '../declarations/DeclarationCreationModal/DeclarationCreationModal';
import { DeclarationCreationOutcomeModal } from '../declarations/DeclarationCreationOutcomeModal/DeclarationCreationOutcomeModal';
import { DeclarationsTable } from '../declarations/DeclarationsTable/DeclarationsTable';
import { StorageCreationModal } from '../locations/StorageCreationModal/StorageCreationModal';
import { StorageTable } from '../locations/StorageTable/StorageTable';

export function StoragePageV2({
  declarationCreationModalOpen,
  declarationCancellationModalOpen,
}: {
  declarationCreationModalOpen?: boolean;
  declarationCancellationModalOpen?: boolean;
}) {
  useTracking();
  const user = useUser();
  const isAuthorized = Boolean(user?.permissions.storage_locations?.view);
  const isAuthorizedToCreateLocations =
    user?.permissions.storage_locations?.create;
  const isAuthorizedToCreateDeclarations =
    user?.permissions.storage_locations?.create_declaration;
  const isAuthorizedToCancelDeclarations =
    user?.permissions.storage_locations?.cancel_declaration;
  const navigate = useNavigate();
  const location = useLocation();
  // TODO make strictly typed using zod
  const { declarationCreationResponse } = location.state ?? {};

  // Get the current tab from the URL pathname
  const selectedTab = location.pathname.includes('declarations')
    ? 'declarations'
    : 'locations';

  const handleTabChange = (key: Key) => {
    navigate(`/storage/${key.toString()}`);
  };

  return (
    <SectionLayout
      isAuthorized={isAuthorized}
      title="Storage"
      data-testid="storage-page"
      actions={
        <>
          {isAuthorizedToCreateLocations && selectedTab === 'locations' && (
            <LinkButton
              size="small"
              data-testid="add-location-button"
              href="/storage/locations/create/details"
              width="auto"
              label="Add Location"
              leftIcon={<FaPlus />}
            />
          )}
          {isAuthorizedToCreateDeclarations &&
            selectedTab === 'declarations' && (
              <LinkButton
                size="small"
                data-testid="add-declaration-button"
                href="/storage/declarations/create"
                width="auto"
                label="Add Declaration"
                leftIcon={<FaPlus />}
              />
            )}
        </>
      }
    >
      <Tabs selectedKey={selectedTab} onSelectionChange={handleTabChange}>
        <TabList>
          <Tab id="declarations">Declarations</Tab>
          <Tab id="locations">Locations</Tab>
        </TabList>
        <TabPanel id="declarations">
          <DeclarationsTable />
        </TabPanel>
        <TabPanel id="locations">
          <StorageTable />
        </TabPanel>
      </Tabs>

      {/* Open state dependant on path */}
      <StorageCreationModal />

      {/* Open state dependant on path */}
      {isAuthorizedToCreateDeclarations && (
        <DeclarationCreationModal
          isOpen={Boolean(declarationCreationModalOpen)}
          isDismissable
          showExitButton
          onExit={() => {
            navigate('/storage');
          }}
          onCreation={(declarationCreationResponse) => {
            navigate('/storage', {
              state: { declarationCreationResponse },
            });
          }}
          navigateToOnCancel="/storage"
        />
      )}

      {isAuthorizedToCancelDeclarations && (
        <DeclarationCancellationModal
          isOpen={Boolean(declarationCancellationModalOpen)}
          navigateToOnSuccess="/storage/declarations"
          onOpenChange={(isOpen: boolean) =>
            !isOpen && navigate('/storage/declarations')
          }
        />
      )}

      {/* Open state dependant on route state */}
      <DeclarationCreationOutcomeModal
        showViewLocationDetailsButton
        isOpen={!!declarationCreationResponse}
        onExit={() =>
          navigate('/storage', {
            state: {},
          })
        }
        {...declarationCreationResponse}
      />
    </SectionLayout>
  );
}
